import { isEmpty } from 'lodash';
import React, { Fragment, ReactNode, useState } from 'react';

import {
  Button,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';

import EditorLayout from 'app/developer-v3/layouts/EditorLayout';
import ManagerLayout from 'app/developer-v3/layouts/ManagerLayout';
import ModalsOnboarding from 'app/developer-v3/components/ModalsOnboarding';
import PartnerSandboxAlert from 'app/developer-v3/components/PartnerSandboxAlert';
import { ROLES, BASE_DOC_URL_VB } from 'app/developer-v3/constants';
import { getTeamUrl } from 'app/developer-v3/utils';
import Link from 'app/common/components/Link';
import {
  CurrentVersionReadOnlyReason,
  useActiveProfile,
  useAddAdmin,
  useAddCollaborator,
  useAddSubscriber,
  useCurrentVersionReadOnlyReason,
} from 'app/developer-v3/hooks';

import TeamMemberModal from './TeamMemberModal';

import type { AppSummary } from 'app/developer-v3/types';

type Props = {
  appSummary: AppSummary;
  children: ReactNode;
  currentUrl: string;
  error?: string;
  hasPartnerRoles?: boolean;
  hasPartnerProfile?: boolean;
  isAdminMasqueraded?: boolean;
  isDeveloper?: boolean;
  isLoadingPartnerProfile?: boolean;
  isSaving: boolean;
  termsAccepted?: boolean;
};

const TeamPage = (props: Props) => {
  const {
    appSummary,
    children,
    currentUrl,
    error,
    hasPartnerRoles,
    hasPartnerProfile,
    isAdminMasqueraded,
    isDeveloper,
    isLoadingPartnerProfile,
    isSaving,
    termsAccepted,
  } = props;

  const [isShowingModal, setIsShowingModal] = useState(false);

  const { appId: integrationId } = appSummary;
  const { activeProfile } = useActiveProfile();
  const { mutate: addAdmin } = useAddAdmin();
  const { mutate: addCollaborator } = useAddCollaborator();
  const { mutate: addSubscriber } = useAddSubscriber();

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isNotAdmin =
    currentVersionReadOnlyReason === CurrentVersionReadOnlyReason.NOT_ADMIN;

  const openModal = () => setIsShowingModal(true);

  const closeModal = () => setIsShowingModal(false);

  const submitTeamMember = formData => {
    const {
      teamMemberEmail: email,
      teamMemberRole: role,
      teamMemberWelcome: message,
    } = formData;

    const args = { email, integrationId, message };

    if (role === ROLES.admin) {
      addAdmin(args);
    } else if (role === ROLES.collaborator) {
      addCollaborator(args);
    } else if (role === ROLES.subscriber) {
      addSubscriber(args);
    }

    setIsShowingModal(false);
  };

  const isOpen = isShowingModal || isSaving || !isEmpty(error);

  // These flags are intentionally made flattened with AND operator. Please
  // AVOID creating dependency between them. It'll be bug-prone because of
  // greater complexity and impaired readability.
  //
  // TODO: Move these modals to Developer or AppBuilder. They ask for agreement
  // to platform terms, which is a requirement for all pages.
  //
  // TODO: These business logics are copied from HomePage. We should extract
  // them to a common place.
  const shouldShowOnBoardingModal =
    !isLoadingPartnerProfile &&
    !isDeveloper &&
    (!termsAccepted || !hasPartnerRoles);
  const shouldShowOnboardingModalExistingDeveloper =
    !isLoadingPartnerProfile &&
    isDeveloper &&
    (!hasPartnerProfile || !hasPartnerRoles) &&
    !isAdminMasqueraded;

  const shouldShowPartnerSandboxAlert = appSummary.isPublished;

  const baseUrl = getTeamUrl(appSummary.appId);
  const navItems = [
    { text: 'Team Members', href: `${baseUrl}/members` },
    ...(!appSummary.isPrivate
      ? [
          {
            text: isNotAdmin ? (
              <Tooltip content="Admin access only">
                <TooltipFocusableTrigger>Settings</TooltipFocusableTrigger>
              </Tooltip>
            ) : (
              'Settings'
            ),
            href: `${baseUrl}/settings`,
            disabled: isNotAdmin,
          },
        ]
      : []),
  ];

  return (
    <ManagerLayout
      afterHeader={<Button onPress={openModal}>Invite Team Member</Button>}
      description={
        <Fragment>
          Invite and manage team members on this integration.{' '}
          <Link
            href={`${BASE_DOC_URL_VB}/manage/add-team#assign-primary-contact-roles`}
            target="_blank"
          >
            Learn more about assigning primary contact roles.
          </Link>
        </Fragment>
      }
      shouldLimitWidth={false}
      title="Manage team"
    >
      <div>
        {shouldShowPartnerSandboxAlert && (
          <PartnerSandboxAlert
            email={activeProfile.email}
            integrationId={integrationId}
          />
        )}
        <EditorLayout
          currentUrl={currentUrl}
          hasHeaderPadding={false}
          navConfig={navItems}
          title={null}
        >
          {children}
        </EditorLayout>
        {isOpen && (
          <TeamMemberModal
            appTitle={appSummary.title}
            error={error}
            integrationId={appSummary.appId}
            onCloseModal={closeModal}
            submitTeamMember={formData => submitTeamMember(formData)}
          />
        )}
        {shouldShowOnBoardingModal && <ModalsOnboarding.NewDev />}
        {shouldShowOnboardingModalExistingDeveloper && (
          <ModalsOnboarding.ExistingDev />
        )}
      </div>
    </ManagerLayout>
  );
};

export default TeamPage;
